import type { OrderCheckpointAttachment, OrderCheckpointRecord, OrderDocumentTypeValue } from '@my/tms-api';
import {
  documentForCheckpointAttachment,
  useAttachCheckpointAttachmentToOrder,
  useOrderCheckpointAttachment,
  useOrderDocuments,
} from '@my/tms-api/hooks';
import { showToast } from '@my/ui';
import { Download, ImageOff, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ORDER_DOCUMENT_TYPE_LABELS } from '@/features/orders/lib/documentTypeOptions';

/**
 * The photos and signature a driver took with a checkpoint on the phone.
 *
 * Shown inside the page only — thumbnails that open full size in an overlay,
 * never a new tab: they are object URLs, and a signature is an SVG, which
 * opened as a page would run its scripts. Same rule as myCheck's evidence.
 *
 * Each can be downloaded, or attached to the order's Documents as one of the
 * document types — linked to the same file, not copied. Attached as a POD it
 * counts for its leg like a POD uploaded by hand (the pill and the invoicing
 * chip read the Documents list).
 */
export function CheckpointEvidence({
  record,
  organisationId,
  orderId,
}: {
  record: OrderCheckpointRecord;
  organisationId: string | null;
  orderId: string | null;
}) {
  const attachments = record.attachments ?? [];
  if (attachments.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-3 pl-6">
      {attachments.map((a) => (
        <Evidence key={a.id} attachment={a} organisationId={organisationId} orderId={orderId} />
      ))}
    </div>
  );
}

function Evidence({
  attachment,
  organisationId,
  orderId,
}: {
  attachment: OrderCheckpointAttachment;
  organisationId: string | null;
  orderId: string | null;
}) {
  const { url, isLoading, isError } = useOrderCheckpointAttachment(organisationId, orderId, attachment.id);
  const signature = attachment.kind === 'Signature';
  return (
    <div className="flex items-start gap-2">
      {signature ? (
        <Signature url={url} isLoading={isLoading} isError={isError} />
      ) : (
        <Photo url={url} isLoading={isLoading} isError={isError} />
      )}
      {url && <EvidenceActions attachment={attachment} url={url} organisationId={organisationId} orderId={orderId} />}
    </div>
  );
}

const NOT_ATTACHED = '';

function EvidenceActions({
  attachment,
  url,
  organisationId,
  orderId,
}: {
  attachment: OrderCheckpointAttachment;
  url: string;
  organisationId: string | null;
  orderId: string | null;
}) {
  const { data: documents } = useOrderDocuments(orderId);
  const attach = useAttachCheckpointAttachmentToOrder(organisationId, orderId);
  const current = documentForCheckpointAttachment(documents, attachment.id);
  const what = attachment.kind === 'Signature' ? 'signature' : 'photo';

  const onAttach = async (type: OrderDocumentTypeValue) => {
    try {
      await attach.mutateAsync({ attachmentId: attachment.id, type });
      showToast({
        type: 'success',
        message: `${current ? 'Now' : 'Attached as'} ${ORDER_DOCUMENT_TYPE_LABELS[type].toLowerCase()} on the order's documents.`,
      });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : `Failed to attach the ${what}.` });
    }
  };

  return (
    <div className="flex w-44 flex-col gap-1">
      <select
        value={current?.type ?? NOT_ATTACHED}
        disabled={attach.isPending}
        onChange={(e) => e.target.value && onAttach(e.target.value as OrderDocumentTypeValue)}
        aria-label={`Attach the ${what} to the order's documents`}
        className="h-6 rounded-sm border border-slate-300 bg-white px-1 text-[11px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-60"
      >
        {!current && (
          <option value={NOT_ATTACHED} disabled>
            Attach to order as…
          </option>
        )}
        {(Object.entries(ORDER_DOCUMENT_TYPE_LABELS) as Array<[OrderDocumentTypeValue, string]>).map(([value, label]) => (
          <option key={value} value={value}>
            {current ? `On documents: ${label}` : label}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-2 text-[11px]">
        <a
          href={url}
          download={fileNameFor(attachment)}
          className="inline-flex items-center gap-1 text-sky-700 hover:underline"
        >
          <Download className="h-3 w-3" /> Download
        </a>
        {attach.isPending && <Loader2 className="h-3 w-3 animate-spin text-slate-400" />}
      </div>
    </div>
  );
}

/** "driver-photo-1a2b3c4d.jpg" — a download, so the SVG is saved, never opened as a page. */
function fileNameFor(a: OrderCheckpointAttachment): string {
  const ext: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/heic': 'heic',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
  };
  const suffix = ext[a.contentType.toLowerCase()];
  return `driver-${a.kind === 'Signature' ? 'signature' : 'photo'}-${a.id.slice(0, 8)}${suffix ? `.${suffix}` : ''}`;
}

interface Loaded {
  url: string | null;
  isLoading: boolean;
  isError: boolean;
}

function Photo({ url, isLoading, isError }: Loaded) {
  const [open, setOpen] = useState(false);
  if (isError)
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
        <ImageOff className="h-3 w-3" /> Photo unavailable
      </span>
    );
  return (
    <>
      <button
        type="button"
        onClick={() => url && setOpen(true)}
        disabled={!url}
        aria-label="Open photo"
        className="block h-16 w-16 overflow-hidden rounded-sm border border-slate-300 bg-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500"
      >
        {isLoading || !url ? (
          <span className="block h-full w-full animate-pulse bg-slate-200" />
        ) : (
          <img src={url} alt="Photo from the driver" className="h-full w-full object-cover" />
        )}
      </button>
      {open && url && <Lightbox url={url} onClose={() => setOpen(false)} />}
    </>
  );
}

function Signature({ url, isLoading, isError }: Loaded) {
  if (isError) return <span className="text-[10px] text-slate-400">Signature unavailable</span>;
  if (isLoading || !url) return <span className="block h-12 w-40 animate-pulse rounded-sm bg-slate-100" />;
  return (
    <span className="inline-block rounded-sm border border-slate-200 bg-white p-1">
      {/* In an <img>, an SVG cannot run script — the reason it is never opened any other way. */}
      <img src={url} alt="Signature" className="h-12 max-w-[12rem] object-contain" />
    </span>
  );
}

function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    // Above the slide-over panel (z-50).
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6" onClick={onClose}>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <img src={url} alt="Photo from the driver" className="max-h-full max-w-full rounded-sm object-contain" onClick={(e) => e.stopPropagation()} />
    </div>
  );
}
